/**
 * AddClientModal — manually add one client to the Review Planner.
 *
 * A single free-text name (stored as display_name; is_entity stays
 * false), a month select (Feb–Nov only), and the two per-client
 * applicability toggles: S14 Fee Renewal and LA Income Review
 * (Annual Review and Commission Splits are always applicable, so they
 * need no toggle here).
 */

import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import {
  MONTH_NAMES,
  OPEN_MONTHS,
  isPastPlannerMonth,
  getCurrentSASTYearMonth,
} from '../../utils/reviewPlanner';
import { createReviewClient } from '../../api/reviewClientsApi';

function ApplicabilityToggle({ label, value, onChange }) {
  return (
    <div className="rv-check-row" style={{ borderBottom: 'none', padding: '0.4rem 0' }}>
      <button
        type="button"
        className={`rv-toggle${value ? ' rv-toggle-on' : ''}`}
        onClick={() => onChange(!value)}
        aria-pressed={value}
        aria-label={label}
      />
      <span className="rv-check-label">{label}</span>
    </div>
  );
}

export default function AddClientModal({ year, defaultMonth, onClose, onAdded }) {
  const [name, setName] = useState('');
  const [month, setMonth] = useState(
    OPEN_MONTHS.includes(defaultMonth) ? defaultMonth : OPEN_MONTHS[0]
  );
  const [s14Applicable, setS14Applicable] = useState(false);
  const [laIncomeApplicable, setLaIncomeApplicable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter the client name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await createReviewClient({
        displayName: trimmed,
        reviewYear: year,
        reviewMonth: month,
        s14Applicable,
        laIncomeApplicable,
      });
      onAdded(created);
    } catch (err) {
      setError(err.message || 'Could not add the client.');
      setSaving(false);
    }
  };

  return (
    <div className="rv-overlay" onClick={onClose}>
      <div className="rv-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Add client">
        <h3 className="rv-modal-title"><UserPlus size={18} /> Add client</h3>

        {error && <div className="rv-error">{error}</div>}

        <div className="rv-field">
          <label className="rv-label" htmlFor="rv-add-name">Client name</label>
          <input
            id="rv-add-name"
            className="rv-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Piet Retief"
            autoFocus
          />
        </div>

        <div className="rv-field">
          <label className="rv-label" htmlFor="rv-add-month">Review month ({year})</label>
          <select
            id="rv-add-month"
            className="rv-select"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {OPEN_MONTHS.map((m) => (
              <option key={m} value={m}>{MONTH_NAMES[m]}</option>
            ))}
          </select>
          {/* Informational only — nothing blocks and nothing auto-moves
              (permanent-anchor principle, §34.4). */}
          {isPastPlannerMonth(year, month, getCurrentSASTYearMonth()) && (
            <div className="rv-note-info">
              {MONTH_NAMES[month]} {year} has already passed; this client will
              show as overdue until completed. For next year's list, switch the
              planner to {year + 1}.
            </div>
          )}
        </div>

        <div className="rv-field">
          <label className="rv-label">Applicable checklist items</label>
          <ApplicabilityToggle
            label="S14 Fee Renewal"
            value={s14Applicable}
            onChange={setS14Applicable}
          />
          <ApplicabilityToggle
            label="LA Income Review"
            value={laIncomeApplicable}
            onChange={setLaIncomeApplicable}
          />
        </div>

        <div className="rv-modal-actions">
          <button className="rv-btn rv-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="rv-btn rv-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Adding…' : 'Add client'}
          </button>
        </div>
      </div>
    </div>
  );
}
